import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { metasAPI, usersAPI, equipesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Target, Plus, Edit, Trash2, Loader2, Trophy,
  TrendingUp, Users, ChevronLeft, ChevronRight,
  CheckCircle, Clock, AlertTriangle, Award
} from 'lucide-react';

const MetasVendas = () => {
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'ADMIN';
  const isGerente = user?.perfil === 'GERENTE';
  const isSupervisor = user?.perfil === 'SUPERVISOR';
  const isVendedor = user?.perfil === 'VENDEDOR';
  const canManage = isAdmin || isGerente || ['BKO'].includes(user?.perfil);

  const [metas, setMetas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [metaEditando, setMetaEditando] = useState(null);
  const [minhaMeta, setMinhaMeta] = useState(null);

  // Mês selecionado
  const [mesRef, setMesRef] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [formData, setFormData] = useState({
    vendedor_id: '',
    vendedor_nome: '',
    meta_quantidade: '',
    periodo: 'mensal',
    mes_referencia: '',
    observacao: ''
  });

  useEffect(() => {
    carregarDados();
    // Atualiza sozinho: vendas que viram "Instalada" no acompanhamento refletem na meta sem recarregar a página
    const id = setInterval(() => { if (!document.hidden) carregarDados(true); }, 60000);
    const onVisibility = () => { if (!document.hidden) carregarDados(true); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisibility); };
  }, [mesRef]);

  const carregarDados = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const promises = [metasAPI.listar({ mes_referencia: mesRef })];
      if (canManage || isSupervisor) {
        promises.push(usersAPI.listVendedores());
        promises.push(equipesAPI.listar());
      }
      if (isVendedor) {
        promises.push(metasAPI.minha());
      }

      const results = await Promise.all(promises);
      setMetas(results[0].data);

      if (canManage || isSupervisor) {
        setVendedores(results[1].data || []);
        setEquipes(results[2].data || []);
      }
      if (isVendedor && results.length > 1) {
        setMinhaMeta(results[1].data);
      }
    } catch (error) {
      toast.error('Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  };

  const mesAnterior = () => {
    const [y, m] = mesRef.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMesRef(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const mesProximo = () => {
    const [y, m] = mesRef.split('-').map(Number);
    const d = new Date(y, m, 1);
    setMesRef(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const mesAtualLabel = useMemo(() => {
    const [y, m] = mesRef.split('-').map(Number);
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${meses[m - 1]} de ${y}`;
  }, [mesRef]);

  // Estatísticas
  const stats = useMemo(() => {
    const total = metas.length;
    const concluidas = metas.filter(m => m.status === 'concluida').length;
    const emAndamento = metas.filter(m => m.status === 'em_andamento').length;
    const totalMeta = metas.reduce((s, m) => s + (m.meta_quantidade || 0), 0);
    const totalInstaladas = metas.reduce((s, m) => s + (m.vendas_instaladas || 0), 0);
    const percentualGeral = totalMeta > 0 ? Math.round((totalInstaladas / totalMeta) * 100) : 0;
    return { total, concluidas, emAndamento, totalMeta, totalInstaladas, percentualGeral };
  }, [metas]);

  const abrirModalNovo = () => {
    setMetaEditando(null);
    setFormData({
      vendedor_id: '', vendedor_nome: '',
      meta_quantidade: '', periodo: 'mensal',
      mes_referencia: mesRef, observacao: ''
    });
    setModalAberto(true);
  };

  const abrirModalEditar = (meta) => {
    setMetaEditando(meta);
    setFormData({
      vendedor_id: meta.vendedor_id,
      vendedor_nome: meta.vendedor_nome,
      meta_quantidade: String(meta.meta_quantidade),
      periodo: meta.periodo,
      mes_referencia: meta.mes_referencia,
      observacao: meta.observacao || ''
    });
    setModalAberto(true);
  };

  const handleSalvar = async () => {
    if (!metaEditando && !formData.vendedor_id) {
      toast.error('Selecione um vendedor');
      return;
    }
    if (!formData.meta_quantidade || Number(formData.meta_quantidade) < 1) {
      toast.error('Informe uma meta valida (minimo 1)');
      return;
    }

    setSalvando(true);
    try {
      if (metaEditando) {
        await metasAPI.atualizar(metaEditando.id, {
          meta_quantidade: Number(formData.meta_quantidade),
          periodo: formData.periodo,
          observacao: formData.observacao || null
        });
        toast.success('Meta atualizada com sucesso');
      } else {
        await metasAPI.criar({
          vendedor_id: formData.vendedor_id,
          vendedor_nome: formData.vendedor_nome,
          meta_quantidade: Number(formData.meta_quantidade),
          periodo: formData.periodo,
          mes_referencia: formData.mes_referencia,
          observacao: formData.observacao || null
        });
        toast.success('Meta criada com sucesso');
      }
      setModalAberto(false);
      carregarDados();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao salvar meta';
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (metaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta meta?')) return;
    try {
      await metasAPI.excluir(metaId);
      toast.success('Meta excluida');
      carregarDados();
    } catch {
      toast.error('Erro ao excluir meta');
    }
  };

  const handleSelectVendedor = (vendedorId) => {
    const v = vendedores.find(u => u.id === vendedorId);
    if (v) {
      setFormData(prev => ({ ...prev, vendedor_id: v.id, vendedor_nome: v.nome }));
    }
  };

  const getProgressColor = (pct) => {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 75) return 'bg-green-500';
    if (pct >= 50) return 'bg-yellow-500';
    if (pct >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressBg = (pct) => {
    if (pct >= 100) return 'bg-red-50 dark:bg-red-950/30';
    if (pct >= 50) return 'bg-yellow-50 dark:bg-yellow-950/30';
    return 'bg-red-50 dark:bg-red-950/30';
  };

  // Vendedor view: card pessoal
  if (isVendedor && minhaMeta) {
    const m = minhaMeta;
    const pct = m.progresso?.percentual || 0;
    return (
      <div className="space-y-6" data-testid="metas-vendas-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Target className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Minha Meta</h1>
            <p className="text-gray-500 dark:text-gray-400">{mesAtualLabel}</p>
          </div>
        </div>

        {!m.tem_meta ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Target className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Nenhuma meta definida para este mes</p>
              <p className="text-gray-400 text-sm mt-1">Seu supervisor ou admin pode definir sua meta</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden" data-testid="minha-meta-card">
            <CardContent className="p-0">
              <div className={`p-8 ${pct >= 100 ? 'bg-gradient-to-r from-red-500 to-green-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'} text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Progresso do Mes</p>
                    <p className="text-5xl font-black mt-1">{pct}%</p>
                  </div>
                  {pct >= 100 ? (
                    <Trophy className="w-20 h-20 text-white/30" />
                  ) : (
                    <Target className="w-20 h-20 text-white/30" />
                  )}
                </div>
                <div className="mt-4 bg-white/20 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700">
                <div className="p-6 text-center">
                  <p className="text-3xl font-bold text-red-600">{m.progresso?.instaladas || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Instaladas</p>
                </div>
                <div className="p-6 text-center">
                  <p className="text-3xl font-bold text-amber-600">{m.progresso?.meta || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Meta</p>
                </div>
                <div className="p-6 text-center">
                  <p className="text-3xl font-bold text-red-500">{m.progresso?.faltam || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Faltam</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Admin/BKO/Supervisor/Gerente view
  return (
    <div className="space-y-6" data-testid="metas-vendas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Target className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Metas de Vendas</h1>
            <p className="text-gray-500 dark:text-gray-400">Acompanhe o progresso da equipe</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Month Selector */}
          <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm px-1">
            <Button size="sm" variant="ghost" onClick={mesAnterior} className="p-1 h-8 w-8" data-testid="mes-anterior">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 text-sm font-semibold text-gray-700 dark:text-gray-200 min-w-[160px] text-center" data-testid="mes-label">
              {mesAtualLabel}
            </span>
            <Button size="sm" variant="ghost" onClick={mesProximo} className="p-1 h-8 w-8" data-testid="mes-proximo">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {canManage && (
            <Button onClick={abrirModalNovo} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white" data-testid="btn-nova-meta">
              <Plus className="w-4 h-4 mr-2" /> Nova Meta
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              <p className="text-xs text-gray-500">Vendedores c/ Meta</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.concluidas}</p>
              <p className="text-xs text-gray-500">Metas Atingidas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{stats.totalInstaladas}/{stats.totalMeta}</p>
              <p className="text-xs text-gray-500">Total Instaladas/Meta</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Award className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{stats.percentualGeral}%</p>
              <p className="text-xs text-gray-500">Progresso Geral</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Barra de progresso geral */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progresso Geral da Equipe</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">{stats.percentualGeral}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${getProgressColor(stats.percentualGeral)}`}
              style={{ width: `${Math.min(stats.percentualGeral, 100)}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Metas List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : metas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Nenhuma meta cadastrada para {mesAtualLabel}</p>
            {canManage && (
              <Button onClick={abrirModalNovo} variant="outline" className="mt-4" data-testid="btn-nova-meta-empty">
                <Plus className="w-4 h-4 mr-2" /> Criar Meta
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {metas.map((meta) => {
            const pct = meta.meta_quantidade > 0
              ? Math.round((meta.vendas_instaladas / meta.meta_quantidade) * 100)
              : 0;
            const atingiu = pct >= 100;

            return (
              <Card
                key={meta.id}
                className={`border-0 shadow-sm hover:shadow-md transition-all overflow-hidden ${atingiu ? 'ring-2 ring-red-400' : ''}`}
                data-testid={`meta-card-${meta.vendedor_id}`}
              >
                {/* Top accent bar */}
                <div className={`h-1.5 ${getProgressColor(pct)}`} />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${atingiu ? 'bg-red-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                        {meta.vendedor_nome?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">{meta.vendedor_nome}</p>
                        <Badge variant={atingiu ? 'default' : 'secondary'} className={`text-[10px] ${atingiu ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : ''}`}>
                          {atingiu ? 'Meta Atingida' : 'Em Andamento'}
                        </Badge>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirModalEditar(meta)} className="h-7 w-7 p-0" data-testid={`btn-editar-meta-${meta.id}`}>
                          <Edit className="w-3.5 h-3.5 text-gray-400" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => handleExcluir(meta.id)} className="h-7 w-7 p-0 hover:text-red-500" data-testid={`btn-excluir-meta-${meta.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-gray-400" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Progress */}
                  <div className={`rounded-lg p-3 ${getProgressBg(pct)}`}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-300 font-medium">
                        {meta.vendas_instaladas} / {meta.meta_quantidade} vendas
                      </span>
                      <span className={`font-bold ${atingiu ? 'text-red-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                        {pct}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${getProgressColor(pct)}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {atingiu
                        ? 'Parabens! Meta atingida!'
                        : `Faltam ${Math.max(0, meta.meta_quantidade - meta.vendas_instaladas)} vendas`
                      }
                    </p>
                  </div>

                  {meta.observacao && (
                    <p className="text-xs text-gray-400 mt-2 italic truncate" title={meta.observacao}>{meta.observacao}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Criar/Editar */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="max-w-md" data-testid="modal-meta">
          <DialogHeader>
            <DialogTitle>{metaEditando ? 'Editar Meta' : 'Nova Meta de Vendas'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!metaEditando && (
              <div>
                <Label>Vendedor</Label>
                <Select value={formData.vendedor_id} onValueChange={handleSelectVendedor}>
                  <SelectTrigger data-testid="select-vendedor">
                    <SelectValue placeholder="Selecione um vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendedores.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {metaEditando && (
              <div>
                <Label>Vendedor</Label>
                <Input value={formData.vendedor_nome} disabled className="bg-gray-50" />
              </div>
            )}

            <div>
              <Label>Meta (Qtd. Vendas Instaladas)</Label>
              <Input
                type="number"
                min="1"
                value={formData.meta_quantidade}
                onChange={e => setFormData(prev => ({ ...prev, meta_quantidade: e.target.value }))}
                placeholder="Ex: 25"
                data-testid="input-meta-quantidade"
              />
            </div>

            <div>
              <Label>Periodo</Label>
              <Select value={formData.periodo} onValueChange={v => setFormData(prev => ({ ...prev, periodo: v }))}>
                <SelectTrigger data-testid="select-periodo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                  <SelectItem value="campanha">Campanha</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!metaEditando && (
              <div>
                <Label>Mes Referencia</Label>
                <Input
                  type="month"
                  value={formData.mes_referencia}
                  onChange={e => setFormData(prev => ({ ...prev, mes_referencia: e.target.value }))}
                  data-testid="input-mes-referencia"
                />
              </div>
            )}

            <div>
              <Label>Observacao (opcional)</Label>
              <Input
                value={formData.observacao}
                onChange={e => setFormData(prev => ({ ...prev, observacao: e.target.value }))}
                placeholder="Ex: Meta campanha de inverno"
                data-testid="input-observacao"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
            <Button
              onClick={handleSalvar}
              disabled={salvando}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              data-testid="btn-salvar-meta"
            >
              {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {metaEditando ? 'Atualizar' : 'Criar Meta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetasVendas;
